import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Joyride, STATUS, type EventData, type Options, type Step } from 'react-joyride';
import { EMPLOYEE_TOUR_STEPS, JOBSEEKER_TOUR_STEPS } from './tourSteps';
import {
  PRODUCT_TOUR_START_EVENT,
  hasCompletedOnboardingIntent,
} from './onboardingCoordinator';
import './product-tour.css';

export type TourMode = 'jobseeker' | 'employee';

type ProductTourProps = {
  mode: TourMode;
  /** Bump to re-run the tour on demand (topbar compass button). */
  requestId?: number;
};

const DONE_KEY_PREFIX = 'rc_product_tour_done:';

/** Routes where the tour may auto-start (dashboard homes — anchors are on screen). */
const HOME_ROUTES = new Set([
  '/jobseeker-dashboard',
  '/referral-dashboard',
  '/referral-dashboard/jobseeker-dashboard',
]);

function isTourDone(mode: TourMode): boolean {
  try {
    return localStorage.getItem(DONE_KEY_PREFIX + mode) === '1';
  } catch {
    return true;
  }
}

function markTourDone(mode: TourMode) {
  try {
    localStorage.setItem(DONE_KEY_PREFIX + mode, '1');
  } catch {
    /* private mode — skip persistence */
  }
}

/** Keep only steps whose target exists and is actually on screen (e.g. sidebar is off-canvas on mobile). */
function visibleSteps(steps: Step[]): Step[] {
  return steps.filter((step) => {
    if (step.target === 'body') return true;
    if (typeof step.target !== 'string') return true;
    const el = document.querySelector(step.target);
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.right > 0 && rect.left < window.innerWidth;
  });
}

/* Tour behavior + theme — premium glassmorphism dark card */
const TOUR_OPTIONS: Partial<Options> = {
  arrowColor: '#12141A',
  backgroundColor: '#12141A',
  overlayColor: 'rgba(0, 0, 0, 0.75)',
  primaryColor: '#6366F1',
  textColor: '#F3F4F6',
  width: 380,
  zIndex: 14000,
  showProgress: true,
  skipBeacon: true,
  buttons: ['back', 'close', 'primary', 'skip'],
  overlayClickAction: false,
  scrollOffset: 96,
  spotlightPadding: 10,
  spotlightRadius: 14,
};

const TOUR_STYLES = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    mixBlendMode: 'normal' as const,
  },
  tooltip: {
    borderRadius: 18,
    border: '1px solid rgba(255, 255, 255, 0.08)',
    padding: '24px 24px 18px',
    boxShadow: '0 20px 40px -15px rgba(0, 0, 0, 0.5), 0 0 1px 1px rgba(255, 255, 255, 0.05)',
    backdropFilter: 'blur(12px)',
    background: 'rgba(18, 20, 26, 0.92)',
  },
  tooltipContent: {
    padding: 0,
    textAlign: 'left' as const,
  },
  buttonPrimary: {
    background: '#6366F1',
    color: '#FFFFFF',
    fontWeight: 600,
    fontSize: 13,
    borderRadius: 10,
    padding: '10px 20px',
    outline: 'none',
    border: 'none',
    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
  },
  buttonBack: {
    color: '#9CA3AF',
    fontSize: 13,
    fontWeight: 500,
  },
  buttonSkip: {
    color: '#6B7280',
    fontSize: 13,
    fontWeight: 500,
  },
  buttonClose: {
    color: '#6B7280',
  },
};

const TOUR_LOCALE = {
  back: 'Back',
  close: 'Close',
  last: 'Finish',
  next: 'Next',
  skip: 'Skip tour',
};

export function ProductTour({ mode, requestId = 0 }: ProductTourProps) {
  const location = useLocation();
  const [run, setRun] = useState(false);
  const [steps, setSteps] = useState<Step[]>([]);
  const autoStartedFor = useRef<TourMode | null>(null);
  const lastRequestId = useRef(requestId);

  const allSteps = useMemo(
    () => (mode === 'employee' ? EMPLOYEE_TOUR_STEPS : JOBSEEKER_TOUR_STEPS),
    [mode]
  );

  const startTour = useCallback(() => {
    // Never run product tour in mobile view (screen width <= 1024px)
    if (typeof window !== 'undefined' && window.innerWidth <= 1024) {
      return;
    }
    // Anchors live in the persistent shell; wait a beat for lazy page content/layout to settle.
    const timer = window.setTimeout(() => {
      const present = visibleSteps(allSteps);
      if (present.length > 1) {
        setSteps(present);
        setRun(true);
      }
    }, 700);
    return () => window.clearTimeout(timer);
  }, [allSteps]);

  // First-visit auto-start, once per mode, only on a dashboard home route.
  useEffect(() => {
    const path = location.pathname.replace(/\/+$/, '') || '/';
    if (!HOME_ROUTES.has(path)) return;
    if (isTourDone(mode) || autoStartedFor.current === mode) return;
    // New job seekers go through the intent + resume onboarding first; that flow
    // fires PRODUCT_TOUR_START_EVENT when it's done. Don't auto-start over it.
    if (mode === 'jobseeker' && !hasCompletedOnboardingIntent()) return;
    autoStartedFor.current = mode;
    return startTour();
  }, [location.pathname, mode, startTour]);

  // Kick off once the first-visit onboarding flow signals it has completed.
  useEffect(() => {
    const onSignal = () => {
      if (isTourDone(mode)) return;
      autoStartedFor.current = mode;
      startTour();
    };
    window.addEventListener(PRODUCT_TOUR_START_EVENT, onSignal);
    return () => window.removeEventListener(PRODUCT_TOUR_START_EVENT, onSignal);
  }, [mode, startTour]);

  // Manual replay from the topbar compass.
  useEffect(() => {
    if (requestId === lastRequestId.current) return;
    lastRequestId.current = requestId;
    setRun(false);
    return startTour();
  }, [requestId, startTour]);

  const handleEvent = useCallback(
    (data: EventData) => {
      if (
        data.status === STATUS.FINISHED ||
        data.status === STATUS.SKIPPED ||
        (data as any).action === 'close'
      ) {
        markTourDone(mode);
        setRun(false);
      }
    },
    [mode]
  );

  if (!run || steps.length === 0) return null;

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      scrollToFirstStep
      onEvent={handleEvent}
      options={TOUR_OPTIONS}
      styles={TOUR_STYLES}
      locale={TOUR_LOCALE}
    />
  );
}
